package com.gloopgo.game.ui

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.gloopgo.game.viewmodel.AuthUiState
import com.gloopgo.game.viewmodel.AuthViewModel
import gloopgo.shared.generated.resources.Res
import gloopgo.shared.generated.resources.splash_auth_failed
import gloopgo.shared.generated.resources.splash_retry_connection
import gloopgo.shared.generated.resources.splash_subtitle
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Duration.Companion.milliseconds

@Composable
fun SplashScreen(
    onSessionChecked: () -> Unit,
    authViewModel: AuthViewModel? = null
) {
    val scale = remember { Animatable(0.5f) }
    val authState by authViewModel?.uiState?.collectAsState()
        ?: remember { mutableStateOf(AuthUiState(isAuthenticated = true, isCheckingSession = false, userId = "preview")) }

    var minSplashPassed by remember { mutableStateOf(false) }
    var hasNavigated by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        authViewModel?.checkSession()
        scale.animateTo(1.0f, animationSpec = tween(600))
        delay(200.milliseconds)
        minSplashPassed = true
    }

    // Only proceed to Main Menu when BOTH the splash animation has completed AND authentication succeeded
    LaunchedEffect(minSplashPassed, authState.isAuthenticated, authState.userId) {
        if (minSplashPassed && authState.isAuthenticated && authState.userId.isNotEmpty() && !hasNavigated) {
            hasNavigated = true
            onSessionChecked()
        }
    }

    // Always Neon Abyss navy — matches Android/iOS native launch screens (no light flash).
    val bgGradient = listOf(Color(0xFF061436), Color(0xFF0B1B44))
    val titleColor = Color.White
    val subtitleColor = Color(0xFF94A3B8)

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Brush.verticalGradient(bgGradient)),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.padding(24.dp)
        ) {
            Box(
                contentAlignment = Alignment.Center,
                modifier = Modifier.size(120.dp)
            ) {
                SplashBackgroundDots(
                    modifier = Modifier.fillMaxSize()
                )
                GloopSplashLogo(
                    modifier = Modifier
                        .fillMaxSize()
                        .scale(scale.value)
                )
            }

            Spacer(modifier = Modifier.height(24.dp))

            Text(
                text = "GLOOPGO",
                fontSize = 36.sp,
                fontWeight = FontWeight.Black,
                color = titleColor,
                letterSpacing = 3.sp
            )

            Text(
                text = stringResource(Res.string.splash_subtitle),
                fontSize = 14.sp,
                color = subtitleColor
            )

            Spacer(modifier = Modifier.height(36.dp))

            if (authState.isCheckingSession || (!minSplashPassed && authState.isAuthenticated)) {
                CircularProgressIndicator(
                    color = Color(0xFF00E5FF),
                    strokeWidth = 3.dp,
                    modifier = Modifier.size(32.dp)
                )
            } else if (!authState.isAuthenticated) {
                Text(
                    text = authState.errorMessage ?: stringResource(Res.string.splash_auth_failed),
                    fontSize = 13.sp,
                    color = Color(0xFFFF5252),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.padding(horizontal = 24.dp)
                )
                Spacer(modifier = Modifier.height(16.dp))
                Button(
                    onClick = { authViewModel?.checkSession() },
                    colors = ButtonDefaults.buttonColors(
                        containerColor = Color(0xFF00E5FF),
                        contentColor = Color(0xFF061436)
                    ),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Text(stringResource(Res.string.splash_retry_connection), fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}

/**
 * Animated Gloop creature splash logo matching the app icon foreground.
 */
@Composable
private fun GloopSplashLogo(modifier: Modifier = Modifier) {
    Canvas(modifier = modifier) {
        val w = size.width
        val h = size.height
        val cx = w / 2f
        val cy = h / 2f

        // 1. Soft radial aura glow
        drawCircle(
            brush = Brush.radialGradient(
                colors = listOf(Color(0x6600E5FF), Color(0x1800E5FF), Color.Transparent),
                center = Offset(cx, cy),
                radius = w * 0.48f
            ),
            radius = w * 0.48f,
            center = Offset(cx, cy)
        )

        // 2. Main Gloop Creature Body
        val bodyRadius = w * 0.32f
        drawCircle(
            brush = Brush.linearGradient(
                colors = listOf(Color(0xFF80F4FF), Color(0xFF00E5FF), Color(0xFF0091EA)),
                start = Offset(cx - bodyRadius, cy - bodyRadius),
                end = Offset(cx + bodyRadius, cy + bodyRadius)
            ),
            radius = bodyRadius,
            center = Offset(cx, cy + h * 0.02f)
        )

        // 3. Glossy highlight curve along top rim
        val highlightPath = Path().apply {
            moveTo(cx - bodyRadius * 0.7f, cy - bodyRadius * 0.35f)
            quadraticTo(cx, cy - bodyRadius * 0.9f, cx + bodyRadius * 0.7f, cy - bodyRadius * 0.35f)
            quadraticTo(cx, cy - bodyRadius * 0.6f, cx - bodyRadius * 0.7f, cy - bodyRadius * 0.35f)
            close()
        }
        drawPath(
            path = highlightPath,
            brush = Brush.verticalGradient(
                colors = listOf(Color.White.copy(alpha = 0.8f), Color.White.copy(alpha = 0.0f)),
                startY = cy - bodyRadius * 0.9f,
                endY = cy - bodyRadius * 0.35f
            )
        )

        // 4. Big Expressive Cartoon Eyes
        val eyeRadius = bodyRadius * 0.26f
        val leftEyeCenter = Offset(cx - bodyRadius * 0.42f, cy - bodyRadius * 0.05f)
        val rightEyeCenter = Offset(cx + bodyRadius * 0.42f, cy - bodyRadius * 0.05f)

        // Left Eye
        drawCircle(color = Color(0xFF061B3B), radius = eyeRadius, center = leftEyeCenter)
        drawCircle(
            color = Color.White,
            radius = eyeRadius * 0.45f,
            center = Offset(leftEyeCenter.x - eyeRadius * 0.2f, leftEyeCenter.y - eyeRadius * 0.25f)
        )
        drawCircle(
            color = Color.White,
            radius = eyeRadius * 0.2f,
            center = Offset(leftEyeCenter.x + eyeRadius * 0.3f, leftEyeCenter.y + eyeRadius * 0.3f)
        )

        // Right Eye
        drawCircle(color = Color(0xFF061B3B), radius = eyeRadius, center = rightEyeCenter)
        drawCircle(
            color = Color.White,
            radius = eyeRadius * 0.45f,
            center = Offset(rightEyeCenter.x - eyeRadius * 0.2f, rightEyeCenter.y - eyeRadius * 0.25f)
        )
        drawCircle(
            color = Color.White,
            radius = eyeRadius * 0.2f,
            center = Offset(rightEyeCenter.x + eyeRadius * 0.3f, rightEyeCenter.y + eyeRadius * 0.3f)
        )

        // 5. Rosy Blush Cheeks
        drawCircle(
            color = Color(0xFF00B0FF).copy(alpha = 0.5f),
            radius = eyeRadius * 0.45f,
            center = Offset(cx - bodyRadius * 0.65f, cy + bodyRadius * 0.25f)
        )
        drawCircle(
            color = Color(0xFF00B0FF).copy(alpha = 0.5f),
            radius = eyeRadius * 0.45f,
            center = Offset(cx + bodyRadius * 0.65f, cy + bodyRadius * 0.25f)
        )

        // 6. Cheerful Smile
        val mouthPath = Path().apply {
            moveTo(cx - bodyRadius * 0.18f, cy + bodyRadius * 0.16f)
            quadraticTo(cx, cy + bodyRadius * 0.32f, cx + bodyRadius * 0.18f, cy + bodyRadius * 0.16f)
        }
        drawPath(
            path = mouthPath,
            color = Color(0xFF061B3B),
            style = Stroke(width = w * 0.024f, cap = StrokeCap.Round)
        )
    }
}

/**
 * Orbiting food pellet dots in the background, matching ic_launcher_background.
 */
@Composable
private fun SplashBackgroundDots(modifier: Modifier = Modifier) {
    Canvas(modifier = modifier) {
        val w = size.width
        val h = size.height

        // 9 Orbiting colorful food pellets with specular highlights matching ic_launcher_background
        val pellets = listOf(
            Triple(Offset(w * 0.250f, h * 0.306f), w * 0.0324f, listOf(Color(0xFFFF80AB), Color(0xFFF50057))), // Pink (top-left)
            Triple(Offset(w * 0.352f, h * 0.204f), w * 0.0278f, listOf(Color(0xFFFFFF8D), Color(0xFFFFD600))), // Yellow (top)
            Triple(Offset(w * 0.667f, h * 0.194f), w * 0.0324f, listOf(Color(0xFFFFD180), Color(0xFFFF6D00))), // Orange (top-right)
            Triple(Offset(w * 0.769f, h * 0.296f), w * 0.0370f, listOf(Color(0xFFB9F6CA), Color(0xFF00E676))), // Lime Green (right)
            Triple(Offset(w * 0.824f, h * 0.481f), w * 0.0324f, listOf(Color(0xFFEA80FC), Color(0xFFAA00FF))), // Purple (mid-right)
            Triple(Offset(w * 0.778f, h * 0.685f), w * 0.0278f, listOf(Color(0xFFFFFF8D), Color(0xFFFFD600))), // Yellow (bottom-right)
            Triple(Offset(w * 0.639f, h * 0.806f), w * 0.0324f, listOf(Color(0xFFEA80FC), Color(0xFFAA00FF))), // Purple (bottom)
            Triple(Offset(w * 0.222f, h * 0.704f), w * 0.0324f, listOf(Color(0xFFB9F6CA), Color(0xFF00E676))), // Green (bottom-left)
            Triple(Offset(w * 0.176f, h * 0.491f), w * 0.0278f, listOf(Color(0xFFFFD180), Color(0xFFFF6D00)))  // Orange (left)
        )

        for ((center, radius, colors) in pellets) {
            drawCircle(
                brush = Brush.radialGradient(
                    colors = colors,
                    center = Offset(center.x - radius * 0.28f, center.y - radius * 0.28f),
                    radius = radius
                ),
                radius = radius,
                center = center
            )
            drawCircle(
                color = Color.White.copy(alpha = 0.85f),
                radius = radius * 0.35f,
                center = Offset(center.x - radius * 0.28f, center.y - radius * 0.28f)
            )
        }
    }
}
